import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { Helmet } from 'react-helmet-async';
import { superAdminApi, type CreateUnitDto } from '../../api/superAdminApi';
import { PageHeader } from '../../components/PageHeader';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import type { Unit } from '../../types/domain';

const slugify = (input: string) =>
  input
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100);

export const UnitsPage = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [editing, setEditing] = useState<Unit | null>(null);
  const [creating, setCreating] = useState(false);
  const [deactivating, setDeactivating] = useState<Unit | null>(null);

  const { data: units = [], isLoading } = useQuery({
    queryKey: ['superadmin', 'units'],
    queryFn: superAdminApi.listUnits,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['superadmin', 'units'] });

  const createMutation = useMutation({
    mutationFn: (dto: CreateUnitDto) => superAdminApi.createUnit(dto),
    onSuccess: () => {
      enqueueSnackbar('Jednotka vytvořena', { variant: 'success' });
      invalidate();
      setCreating(false);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: CreateUnitDto & { isActive?: boolean; canHideFromLanding?: boolean } }) =>
      superAdminApi.updateUnit(id, dto),
    onSuccess: () => {
      enqueueSnackbar('Jednotka uložena', { variant: 'success' });
      invalidate();
      setEditing(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => superAdminApi.deactivateUnit(id),
    onSuccess: () => {
      enqueueSnackbar('Jednotka deaktivována', { variant: 'success' });
      invalidate();
      setDeactivating(null);
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const columns: GridColDef<Unit>[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Název SDH', flex: 1, minWidth: 200 },
    { field: 'slug', headerName: 'Slug (URL)', width: 180 },
    {
      field: 'isActive',
      headerName: 'Stav',
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={row.isActive ? 'Aktivní' : 'Deaktivovaná'}
          color={row.isActive ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Akce',
      width: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row">
          <Tooltip title={row.isActive ? 'Spravovat jednotku' : 'Jednotka je deaktivovaná'}>
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => navigate(`/admin?unitId=${row.id}`)}
                disabled={!row.isActive}
              >
                <ManageAccountsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Upravit">
            <IconButton size="small" onClick={() => setEditing(row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.isActive ? 'Deaktivovat' : 'Již deaktivovaná'}>
            <span>
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeactivating(row)}
                disabled={!row.isActive}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <title>Jednotky | SUPER ADMIN</title>
      </Helmet>
      <PageHeader
        title="Jednotky SDH"
        subtitle="Správa všech jednotek v systému"
        actions={
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => setCreating(true)}>
            Nová jednotka
          </Button>
        }
      />
      {isMobile ? (
        <Stack spacing={1}>
          {isLoading && <Typography color="text.secondary">Načítám…</Typography>}
          {!isLoading && units.length === 0 && (
            <Typography color="text.secondary">Žádné jednotky.</Typography>
          )}
          {units.map((u) => (
            <Paper key={u.id} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography fontWeight={600} noWrap>
                    {u.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    /{u.slug}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={u.isActive ? 'Aktivní' : 'Deaktivovaná'}
                      color={u.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Stack direction="row" sx={{ flexShrink: 0 }}>
                  <Tooltip title={u.isActive ? 'Spravovat' : 'Deaktivovaná'}>
                    <span>
                      <IconButton
                        size="small"
                        color="primary"
                        disabled={!u.isActive}
                        onClick={() => navigate(`/admin?unitId=${u.id}`)}
                      >
                        <ManageAccountsIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <IconButton size="small" onClick={() => setEditing(u)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={!u.isActive}
                    onClick={() => setDeactivating(u)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
      ) : (
        <Box sx={{ height: 'calc(100vh - 220px)', bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={units}
            columns={columns}
            loading={isLoading}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </Box>
      )}

      <UnitFormDialog
        open={creating || !!editing}
        unit={editing}
        loading={createMutation.isPending || updateMutation.isPending}
        onCancel={() => {
          setEditing(null);
          setCreating(false);
        }}
        onSubmit={(dto) => {
          if (editing) {
            updateMutation.mutate({
              id: editing.id,
              dto: {
                name: dto.name,
                slug: dto.slug,
                isActive: editing.isActive,
                canHideFromLanding: dto.canHideFromLanding,
              },
            });
          } else {
            createMutation.mutate({ name: dto.name, slug: dto.slug });
          }
        }}
      />

      <ConfirmDialog
        open={!!deactivating}
        title="Deaktivovat jednotku?"
        message={`Opravdu chcete deaktivovat jednotku "${deactivating?.name}"? Bude označena jako neaktivní (lze obnovit).`}
        destructive
        loading={deactivateMutation.isPending}
        onCancel={() => setDeactivating(null)}
        onConfirm={() => deactivating && deactivateMutation.mutate(deactivating.id)}
      />
    </>
  );
};

interface UnitFormDialogProps {
  open: boolean;
  unit: Unit | null;
  loading: boolean;
  onCancel: () => void;
  onSubmit: (dto: CreateUnitDto & { canHideFromLanding?: boolean }) => void;
}

const UnitFormDialog = ({ open, unit, loading, onCancel, onSubmit }: UnitFormDialogProps) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [touchedSlug, setTouchedSlug] = useState(false);
  const [canHideFromLanding, setCanHideFromLanding] = useState(false);

  const handleEnter = () => {
    if (unit) {
      setName(unit.name);
      setSlug(unit.slug);
      setTouchedSlug(true);
      setCanHideFromLanding(!!unit.canHideFromLanding);
    } else {
      setName('');
      setSlug('');
      setTouchedSlug(false);
      setCanHideFromLanding(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth TransitionProps={{ onEnter: handleEnter }}>
      <DialogTitle>{unit ? 'Upravit jednotku' : 'Nová jednotka SDH'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Název SDH"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!touchedSlug) setSlug(slugify(e.target.value));
            }}
            fullWidth
            required
            autoFocus
          />
          <TextField
            label="Slug (URL identifikátor)"
            value={slug}
            onChange={(e) => {
              setSlug(slugify(e.target.value));
              setTouchedSlug(true);
            }}
            fullWidth
            required
            helperText="Malá písmena, číslice a pomlčka. Použito v URL veřejného profilu /jméno-sdh."
          />
          {unit && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={canHideFromLanding}
                    onChange={(e) => setCanHideFromLanding(e.target.checked)}
                  />
                }
                label="Povolit skrytí soutěží z rozcestníku"
              />
              <Alert severity="info" variant="outlined" sx={{ mt: 0.5 }}>
                Když je zapnuto, admin této jednotky může u jednotlivých soutěží zaškrtnout, že
                se nemají zobrazovat na úvodní stránce <code>souteze.hasicionline.cz</code>.
                Soutěž zůstává přístupná přes přímý odkaz <code>/s/&#123;shortCode&#125;</code>.
                Default vypnuto = stávající chování (LIVE soutěž je vidět na rozcestníku).
              </Alert>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          onClick={() => onSubmit({ name, slug, canHideFromLanding })}
          disabled={loading || !name || !slug}
        >
          {unit ? 'Uložit' : 'Vytvořit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
